import { Card, List, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  PipelineStepId,
  PipelineStepState,
} from '~/app/components/pipeline-debug/PipelineDebug/types';

const { Text } = Typography;

type ChecklistItemId =
  | 'rawToLin'
  | 'linToAdie'
  | 'linToIcs'
  | 'adieToIcs'
  | 'icsToDe';

const CHECKLIST_ITEMS: Array<{
  id: ChecklistItemId;
  stepId: PipelineStepId;
}> = [
  { id: 'rawToLin', stepId: 'lin' },
  { id: 'linToAdie', stepId: 'adie' },
  { id: 'linToIcs', stepId: 'ics' },
  { id: 'adieToIcs', stepId: 'ics' },
  { id: 'icsToDe', stepId: 'de' },
];

function statusColor(
  status: PipelineStepState['status'],
): 'default' | 'processing' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'running':
      return 'processing';
    case 'completed':
      return 'success';
    case 'error':
      return 'error';
    case 'ready':
      return 'warning';
    default:
      return 'default';
  }
}

export type PipelinePassChecklistProps = {
  steps: Record<PipelineStepId, PipelineStepState>;
  activeStepId: PipelineStepId;
};

export function PipelinePassChecklist({
  steps,
  activeStepId,
}: PipelinePassChecklistProps) {
  const { t } = useTranslation('manualUpload');

  return (
    <Card
      title={t('supervisor.checklist.title')}
      className="pipeline-debug-card"
      styles={{ body: { padding: 16 } }}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('supervisor.checklist.intro')}
      </Text>
      <List
        size="small"
        dataSource={CHECKLIST_ITEMS}
        renderItem={(item) => {
          const step = steps[item.stepId];
          const isActive = activeStepId === item.stepId;

          return (
            <List.Item
              style={{
                background: isActive ? 'rgba(30, 109, 246, 0.06)' : undefined,
                borderRadius: 6,
                paddingInline: 8,
              }}
            >
              <List.Item.Meta
                title={
                  <span>
                    {t(`supervisor.checklist.items.${item.id}.title`)}{' '}
                    <Tag color={statusColor(step.status)}>
                      {t(`supervisor.checklist.status.${step.status}`)}
                    </Tag>
                  </span>
                }
                description={t(
                  `supervisor.checklist.items.${item.id}.description`,
                )}
              />
            </List.Item>
          );
        }}
      />
    </Card>
  );
}
